import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Stack,
  Alert,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import { useIsMobile } from '../../hooks';
import api from '../../services/api';
import { grey } from '../../styles/tokens';

/**
 * Page Profil Admin
 * Utilise les endpoints /api/admin/auth/profile
 */
const AdminProfile = () => {
  const { user, logout, refreshAuthToken } = useContext(AuthContext);
  const { isMobile } = useIsMobile();
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const data = await api.get('/admin/auth/profile');
      setProfile({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        password: '',
        confirmPassword: '',
      });
    } catch (err) {
      console.error('Erreur lors du chargement du profil:', err);
      setError('Impossible de charger le profil.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePassword = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMessage('');

    if (profile.password && !validatePassword(profile.password)) {
      setError(
        'Le mot de passe doit contenir au moins 8 caracteres, une majuscule, un chiffre et un caractere special.'
      );
      setSaving(false);
      return;
    }

    if (profile.password !== profile.confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      setSaving(false);
      return;
    }

    try {
      const updateData = {
        firstName: profile.firstName,
        lastName: profile.lastName,
      };

      if (profile.password) {
        updateData.password = profile.password;
      }

      await api.put('/admin/auth/profile', updateData);
      setSuccessMessage('Profil mis a jour avec succes.');
      setProfile(prev => ({ ...prev, password: '', confirmPassword: '' }));
      // Rafraîchir le token pour mettre à jour le contexte avec les nouvelles données
      await refreshAuthToken();
    } catch (err) {
      console.error('Erreur lors de la mise a jour:', err);
      setError(err.message || 'Erreur lors de la mise a jour du profil.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 480 }}>
      {/* Header */}
      <Typography variant="h1" sx={{ mb: 3 }}>
        Profil
      </Typography>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Profile Form */}
      <Card>
        <CardContent>
          <Typography variant="h4" sx={{ mb: 2.5 }}>
            Informations personnelles
          </Typography>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Prenom"
                  name="firstName"
                  value={profile.firstName}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Nom"
                  name="lastName"
                  value={profile.lastName}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                />
              </Stack>

              <TextField
                label="Email"
                name="email"
                value={profile.email}
                disabled
                fullWidth
                size="small"
                helperText="L'email ne peut pas etre modifie"
              />

              <Divider sx={{ my: 1 }} />

              <Typography variant="caption">
                Laisser vide pour conserver le mot de passe actuel
              </Typography>

              <TextField
                label="Nouveau mot de passe"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={profile.password}
                onChange={handleInputChange}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Confirmer le mot de passe"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={profile.confirmPassword}
                onChange={handleInputChange}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant="contained"
                type="submit"
                fullWidth
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Mettre a jour'}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Informations du compte
          </Typography>
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: grey[500] }}>Role</Typography>
              <Typography variant="body1">{user?.role}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: grey[500] }}>ID</Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {user?._id}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Logout Button - Mobile only */}
      {isMobile && (
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{
            mt: 3,
            py: 1.5,
            color: grey[600],
            borderColor: grey[300],
            '&:hover': {
              bgcolor: grey[50],
              borderColor: grey[400],
            },
          }}
        >
          Se deconnecter
        </Button>
      )}
    </Box>
  );
};

export default AdminProfile;
